import { AlertCircle, Ban, FileQuestion, History, KeyRound, PlugZap, TimerReset, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, EmptyState, Spinner } from '../ui';
import { errorDetail, requestIdOf } from './status.js';
import './states.css';

/**
 * The one renderer for every non-happy-path state a view can be in (spec §11):
 * loading, partial/streaming, empty, error, offline, permission-denied,
 * session-expired, no source and stale source.
 *
 * Every case follows the same three-part shape, because that is what makes the
 * states feel like one system instead of eight improvisations:
 *   1. a plain-language headline (never a status code),
 *   2. an explanation in normal German of what this means for the user,
 *   3. exactly one concrete next action (plus optional extras).
 *
 * Views must not invent their own placeholder markup — that is how the eight
 * mandated states drift apart per screen.
 *
 * @param {object} props
 * @param {import('./status.js').ViewStatus} props.status
 * @param {import('react').ReactNode} [props.title] - overrides the default headline.
 * @param {import('react').ReactNode} [props.description] - overrides the default explanation.
 * @param {unknown} [props.error] - technical detail; rendered as secondary copy only.
 * @param {string} [props.requestId] - shown verbatim so support can find the request;
 *   auto-extracted from `error` when omitted.
 * @param {() => void} [props.onRetry] - retry action for error/offline/stale states.
 * @param {() => void} [props.onSignIn] - session state; defaults to navigating to /login.
 * @param {() => void} [props.onConnectSource] - noSource state; defaults to /settings.
 * @param {() => void} [props.onRefresh] - staleSource state; falls back to `onRetry`.
 * @param {() => void} [props.onGoBack] - permission state; defaults to history back.
 * @param {import('react').ReactNode} [props.action] - extra action rendered next to the default one.
 * @param {boolean} [props.busy=false] - the action is running; disables it and swaps the label.
 * @param {boolean} [props.compact=false] - denser variant for narrow rails and inline slots.
 * @param {string} [props.className]
 */
export function ViewState({
  status = 'loading',
  title,
  description,
  error,
  requestId,
  onRetry,
  onSignIn,
  onConnectSource,
  onRefresh,
  onGoBack,
  action,
  busy = false,
  compact = false,
  className = '',
  ...rest
}) {
  const { t } = useTranslation();

  if (status === 'loading' || status === 'partial') {
    return (
      <div
        className={`etch-state ${className}`.trim()}
        data-status={status}
        data-compact={compact || undefined}
        role="status"
        aria-live="polite"
        {...rest}
      >
        <Spinner size={compact ? undefined : 'lg'} />
        <p className="etch-state__title">
          {title || (status === 'partial' ? t('states.partial') : t('states.loading'))}
        </p>
        {description && <p className="etch-state__hint">{description}</p>}
      </div>
    );
  }

  const meta = STATUS_META[status] || STATUS_META.error;
  const { Icon } = meta;
  const detail = status === 'error' ? errorDetail(error) : null;
  const traceId = requestId || requestIdOf(error);

  const primary = resolvePrimaryAction(status, { t, onRetry, onSignIn, onConnectSource, onRefresh, onGoBack });

  return (
    <div
      className={`etch-state ${className}`.trim()}
      data-status={status}
      data-compact={compact || undefined}
      role={status === 'error' || status === 'offline' || status === 'quotaExceeded' ? 'alert' : undefined}
      {...rest}
    >
      <EmptyState
        icon={<Icon size={compact ? 20 : 28} />}
        title={title || t(meta.titleKey)}
        action={
          (primary || action) && (
            <div className="etch-state__actions">
              {primary && (
                <Button variant="secondary" size={compact ? 'compact' : undefined} onClick={primary.onClick} disabled={busy}>
                  {busy ? t('states.retrying') : primary.label}
                </Button>
              )}
              {action}
            </div>
          )
        }
      >
        <p className="etch-state__hint">{description ?? t(meta.hintKey)}</p>
        {detail && <p className="etch-state__detail">{detail}</p>}
        {traceId && <p className="etch-state__detail">{t('states.requestId', { id: traceId })}</p>}
      </EmptyState>
    </div>
  );
}

/**
 * Icon + copy per state. Kept as data so a new state cannot be added without
 * also getting a headline and an explanation.
 */
const STATUS_META = {
  empty: { Icon: FileQuestion, titleKey: 'states.empty', hintKey: 'states.emptyHint' },
  error: { Icon: AlertCircle, titleKey: 'states.error', hintKey: 'states.errorHint' },
  offline: { Icon: WifiOff, titleKey: 'states.offline', hintKey: 'states.offlineHint' },
  permission: { Icon: KeyRound, titleKey: 'states.permissionDenied', hintKey: 'states.permissionHint' },
  session: { Icon: TimerReset, titleKey: 'states.sessionExpired', hintKey: 'states.sessionHint' },
  noSource: { Icon: PlugZap, titleKey: 'states.noSource', hintKey: 'states.noSourceHint' },
  staleSource: { Icon: History, titleKey: 'states.staleSource', hintKey: 'states.staleSourceHint' },
  quotaExceeded: { Icon: Ban, titleKey: 'states.quotaExceeded', hintKey: 'states.quotaExceededHint' }
};

/**
 * Every state except `empty` ships a working default action, so a view that
 * only passes `status` still gives the user a way forward. Callers override by
 * passing the matching handler.
 */
function resolvePrimaryAction(status, { t, onRetry, onSignIn, onConnectSource, onRefresh, onGoBack }) {
  switch (status) {
    case 'error':
      return onRetry ? { label: t('states.retry'), onClick: onRetry } : null;
    case 'offline':
      return { label: t('states.checkConnection'), onClick: onRetry || (() => window.location.reload()) };
    case 'session':
      return { label: t('states.signIn'), onClick: onSignIn || (() => navigateTo('/login')) };
    case 'permission':
      return { label: t('states.goBack'), onClick: onGoBack || (() => goBack()) };
    case 'noSource':
      return { label: t('states.connectSource'), onClick: onConnectSource || (() => navigateTo('/settings')) };
    case 'staleSource':
      return onRefresh || onRetry ? { label: t('states.refreshSource'), onClick: onRefresh || onRetry } : null;
    case 'quotaExceeded':
      // A used-up monthly budget is not a transient failure — offering Retry
      // would just fail again until the tenant's administration acts.
      return null;
    default:
      return onRetry ? { label: t('states.retry'), onClick: onRetry } : null;
  }
}

// A hard navigation rather than `useNavigate`: ViewState is also rendered from
// error fallbacks that can sit outside the router, and a state that throws
// while explaining another failure is the worst possible outcome.
function navigateTo(path) {
  if (typeof window !== 'undefined') window.location.assign(path);
}

function goBack() {
  if (typeof window === 'undefined') return;
  if (window.history.length > 1) window.history.back();
  else window.location.assign('/');
}

export default ViewState;
